import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "../lib/api";
import { clearCache, setCacheOwner } from "../lib/analyticsSwr";

const AuthContext = createContext(null);

/**
 * Point the Analytics sessionStorage cache at whoever is signed in.
 *
 * That cache holds a whole workspace's money — gross sales, refunds, per-funnel
 * revenue, buyer counts — and sessionStorage survives a logout in the same tab.
 * Without this, user B signing in after user A on the same machine paints A's
 * figures under B's default range and calls them "stale", which is not a
 * privacy leak the "Updating…" pill can excuse. Cleared on the way out AND on
 * the way in, and stamped with the user id so a clear that fails (blocked
 * storage) still cannot produce a key collision.
 */
function bindAnalyticsCache(user) {
  setCacheOwner((user && user.id) || "");
  clearCache();
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/api/auth/me");
      setCacheOwner((data && data.id) || "");
      setUser(data);
    } catch {
      bindAnalyticsCache(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/api/auth/login", { email, password });
    bindAnalyticsCache(data);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout", {});
    } catch (err) {
      // Best-effort logout — even if the upstream call fails, clear the
      // client state so the user is logged out locally. Surface the error
      // for debuggability instead of swallowing it silently.
      console.warn("logout request failed", err);
    }
    bindAnalyticsCache(null);
    setUser(null);
  }, []);

  const isRole = useCallback((min) => {
    if (!user) return false;
    const rank = { viewer: 1, editor: 2, admin: 3, owner: 4 };
    return rank[user.role] >= rank[min];
  }, [user]);

  // Memoise the context value so consumers don't re-render every time the
  // provider renders. Without this every page that calls useAuth() would
  // re-render whenever AuthProvider's parent re-rendered.
  const value = useMemo(
    () => ({ user, loading, login, logout, refresh, isRole }),
    [user, loading, login, logout, refresh, isRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
